/**
 * useHeartbeat — React hook
 * ─────────────────────────
 * Fetches initial heartbeat status for all agents via REST,
 * then updates live via Socket.io events.
 *
 * Usage:
 *   const { heartbeats, getAgentStatus, unreadCount } = useHeartbeat(userId);
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { io } from "socket.io-client";

// Status priority for sorting (critical first)
const STATUS_PRIORITY = { critical: 0, offline: 1, warning: 2, healthy: 3 };

// Backend base URL — never the React dev server
const BACKEND = process.env.REACT_APP_API_URL || "http://localhost:5000";

/** Get auth headers for fetch calls */
function authHeaders() {
  const token = localStorage.getItem("authToken") || localStorage.getItem("token") || "";
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export function useHeartbeat(userId) {
  // Map of agentId → latest heartbeat result
  const [heartbeats, setHeartbeats] = useState({});
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const socketRef = useRef(null);
  const seenRef = useRef(new Set()); // Track which heartbeats have been "seen"

  // Fetch initial status for all agents
  useEffect(() => {
    if (!userId) return;

    async function fetchInitial() {
      try {
        const res = await fetch(
          `${BACKEND}/api/heartbeat/status?userId=${userId}`,
          { headers: authHeaders() }
        );
        const { heartbeats: data } = await res.json();

        const map = {};
        let unread = 0;
        (data || []).forEach(h => {
          map[h.agentId] = h;
          if (h.status !== "healthy") unread++;
        });

        setHeartbeats(map);
        setUnreadCount(unread);
      } catch (err) {
        console.error("[useHeartbeat] Failed to fetch initial status:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchInitial();
  }, [userId]);

  // Subscribe to live socket updates
  useEffect(() => {
    if (!userId) return;

    const socket = io(BACKEND, {
      auth: { userId },
    });

    socket.on("connect", () => {
      socket.emit("join", `user:${userId}`);
    });

    socket.on("heartbeat", (data) => {
      setHeartbeats(prev => ({
        ...prev,
        [data.agentId]: {
          agentId: data.agentId,
          agentName: data.agentName,
          status: data.status,
          message: data.message,
          actionLabel: data.actionLabel,
          actionRoute: data.actionRoute,
          createdAt: data.timestamp,
        },
      }));

      // Increment unread if not healthy and not already seen
      if (data.status !== "healthy" && !seenRef.current.has(`${data.agentId}-${data.timestamp}`)) {
        seenRef.current.add(`${data.agentId}-${data.timestamp}`);
        setUnreadCount(n => n + 1);
      }
    });

    socketRef.current = socket;
    return () => socket.disconnect();
  }, [userId]);

  // Get status for a specific agent
  const getAgentStatus = useCallback((agentId) => {
    return heartbeats[agentId] || null;
  }, [heartbeats]);

  // Get all non-healthy agents sorted by priority
  const getAlerts = useCallback(() => {
    return Object.values(heartbeats)
      .filter(h => h.status !== "healthy")
      .sort((a, b) => (STATUS_PRIORITY[a.status] ?? 9) - (STATUS_PRIORITY[b.status] ?? 9));
  }, [heartbeats]);

  // Mark all as read (clears unread count)
  const markAllRead = useCallback(() => {
    setUnreadCount(0);
  }, []);

  // Manually trigger a heartbeat check for one agent
  const triggerCheck = useCallback(async (agentId) => {
    try {
      const res = await fetch(
        `${BACKEND}/api/heartbeat/${agentId}/trigger`,
        {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ userId }),
        }
      );
      const json = await res.json();
      const result = json.result;

      if (result) {
        setHeartbeats(prev => ({
          ...prev,
          [agentId]: { ...prev[agentId], ...result, createdAt: new Date().toISOString() },
        }));
      }

      return result;
    } catch (err) {
      console.error("[useHeartbeat] Trigger failed:", err);
    }
  }, [userId]);

  // Register browser push subscription
  const registerPush = useCallback(async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;

    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: process.env.REACT_APP_VAPID_PUBLIC_KEY,
    });

    await fetch(`${BACKEND}/api/heartbeat/push-subscribe`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ userId, subscription: sub }),
    });
  }, [userId]);

  return {
    heartbeats,
    loading,
    unreadCount,
    getAgentStatus,
    getAlerts,
    markAllRead,
    triggerCheck,
    registerPush,
  };
}
